import React from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import AboutSection from "./components/AboutSection";
import SolutionsSection from "./components/SolutionsSection";
import DoctorSection from "./components/DoctorSection";
import ServicesSection from "./components/ServicesSection";
import CareSteps from "./components/CareSteps";
import Testimonials from "./components/Testimonials";
import FAQ from "./components/FAQ";
import AboutHero from "./components/AboutHero";
import ServicesHero from "./components/ServicesHero";
import ServicesHeroSection from "./components/ServicesHeroSection";
import BlogsPage from "./components/BlogsPage";
import BlogDetailsPage from "./components/BlogDetailsPage";
import KeepReadingSection from "./components/KeepReadingSection";
import Footer from "./components/Footer";
import DoctorHeroSection from "./components/DoctorHeroSection";
import DrShrutiPage from "./components/DrShrutiPage";
import DrShrutiEducation from "./components/DrShrutiEducation";
import ProfessionalExperience from "./components/ProfessionalExperience";
import Specialization from "./components/Specialization";
import ResearchPublications from "./components/ResearchPublications.module";
import AwardsMemberships from "./components/AwardsMemberships.module";
import ExperienceDetails from "./components/ExperienceDetails.module";
import FeatureGridSection from "./components/FeatureGridSection.module";
import BlogsOverview from "./components/BlogsOverview";
import DentalPracticeCollage from "./components/GallerySection";
import FloatingButton from "./components/FloatingButton";
import AppointmentPage from "./components/AppointmentPage";
import AppointmentForm from "./components/AppointmentForm";
import ContactInfo from "./components/ContactInfo";
import SEO from "./components/SEO"; // ✅ import SEO component
import InstagramReels from "./components/InstagramReels";
// ScrollToTop Component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);
  return null;
};
// App Content Wrapper
const AppContent = () => {
  const location = useLocation();
  const showFloatingButton = location.pathname !== "/appointment";
  const baseUrl = "https://krisaragastro.com";
  return (
    <>
      <Header />
      {showFloatingButton && <FloatingButton />}
      <ScrollToTop />
      <Routes>
        {/* Home / Landing Page */}
        <Route
          path="/"
          element={
            <>
              <SEO
                description="Krisara Gastroenterology offers expert treatment for digestive and liver disorders. Visit the best gastroenterology and liver care clinic in Hyderabad for advanced care."
                keywords="gastroenterologist hyderabad, liver specialist hyderabad, digestive health clinic, krisara gastro, gastro hospital hyderabad"
                image={`${baseUrl}/images/krisara-banner.jpg`}
                url={`${baseUrl}/`}
                schema={{
 "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": "Krisara Gastroenterology",
    "image": "https://krisaragastro.com/images/clinic-front.jpg",
    "@id": "https://krisaragastro.com",
    "url": "https://krisaragastro.com",
    "telephone": "+91 7569541532",
    "priceRange": "₹₹",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "3rd Floor, Fairmount Square, Pet Basheerabad, Quthbullapur",
      "addressLocality": "Kompally, Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500014",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 17.530123531669847,
      "longitude": 78.4876160846564
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday"
        ],
        "opens": "09:00",
        "closes": "20:00"
      }
    ],
    "sameAs": [
      "https://wa.link/zm2yn8",
      "https://www.instagram.com/dr_shruti.sagar/",
      "https://www.facebook.com/p/Krisara-Gastro-and-Liver-Clinic-61568228834263/"
    ]
                }}
              />
              <HeroSection />
              <AboutSection />
              <SolutionsSection />
              <DoctorSection />
              <ServicesSection />
              <BlogsOverview />
              <CareSteps />
              <Testimonials />
              <FAQ />
              <DentalPracticeCollage />
              <ContactInfo />
              <AppointmentForm />
            </>
          }
        />
        {/* About Page */}
        <Route
          path="/about"
          element={
            <>
              <SEO
                title="About Krisara Gastroenterology | Leading Gastro & Liver Care Clinic in Hyderabad"
                description="Learn about Krisara Gastroenterology — a trusted digestive and liver care center in Hyderabad led by expert gastroenterologists and hepatologists."
                keywords="about krisara gastro, gastro clinic hyderabad, liver care doctors, digestive health experts"
                image={`${baseUrl}/images/about-banner.jpg`}
                url={`${baseUrl}/about`}
              />
              <AboutHero />
              <Testimonials />
              <BlogsOverview />
              <DentalPracticeCollage />
              <AppointmentForm />
            </>
          }
        />
        {/* Blogs Page */}
        <Route
          path="/blogs"
          element={
            <>
              <SEO
                title="Digestive Health & Liver Care Blogs | Krisara Gastroenterology"
                description="Read expert blogs from Krisara Gastro’s specialists on digestive wellness, liver health, and preventive gastro care."
                keywords="gastro blogs, digestive health tips, liver wellness, krisara blog"
                image={`${baseUrl}/images/blogs-banner.jpg`}
                url={`${baseUrl}/blogs`}
              />
              <BlogsPage />
            </>
          }
        />
        {/* Blog Details */}
        <Route
          path="/blogs/:id"
          element={
            <>
              <SEO
                title="Gastroenterology Blog | Digestive & Liver Health Insights | Krisara Gastro"
                description="In-depth guides and tips on digestive and liver care from the specialists at Krisara Gastroenterology, Hyderabad."
                keywords="gastro health articles, liver care tips, digestive wellness"
                image={`${baseUrl}/images/blog-detail.jpg`}
                url={`${baseUrl}/blogs/:id`}
              />
              <BlogDetailsPage />
              <KeepReadingSection />
              <AppointmentForm />
            </>
          }
        />
        {/* Services Page */}
        <Route
          path="/services"
          element={
            <>
              <SEO
                title="Our Gastro & Liver Care Services | Krisara Gastroenterology Hyderabad"
                description="Explore our comprehensive gastroenterology services — endoscopy, colonoscopy, liver treatment, acidity, IBS, and digestive disorder management."
                keywords="endoscopy hyderabad, colonoscopy, liver disease treatment, gastro services, stomach specialist"
                image={`${baseUrl}/images/services-banner.jpg`}
                url={`${baseUrl}/services`}
              />
              <ServicesHero />
              <ServicesHeroSection />
              <BlogsOverview />
              <Testimonials />
              <DentalPracticeCollage />
              <AppointmentForm />
            </>
          }
        />
        {/* Doctor Page */}
        <Route
          path="/doctor"
          element={
            <>
              <SEO
                title="Dr. Shruti Sagar| Leading Gastroenterologist & Liver Specialist in Hyderabad"
                description="Meet Dr. Shruti Sagar,  expert gastroenterologist at Krisara Gastro, Hyderabad. Specializing in digestive, liver, and pancreatic diseases."
                keywords="dr shruti sagar gastroenterologist, hepatologist hyderabad, digestive specialist, krisara doctor"
                image={`${baseUrl}/images/doctor.jpg`}
                url={`${baseUrl}/doctor`}
              />
              <DoctorHeroSection />
              <DrShrutiPage />
              <DrShrutiEducation />
              <ProfessionalExperience />
              <Specialization />
              <ResearchPublications />
              <AwardsMemberships />
              <ExperienceDetails />
              <FeatureGridSection />
              <Testimonials />
              <BlogsOverview />
              <DentalPracticeCollage />
              <AppointmentForm />
            </>
          }
        />
        {/* FAQ Page */}
        <Route
          path="/faq"
          element={
            <>
              <SEO
                title="FAQs | Krisara Gastroenterology Hyderabad"
                description="Find answers to common questions about gastroenterology treatments, liver care, and digestive health procedures."
                keywords="gastro faqs, digestive health questions, liver treatment faqs, krisara clinic"
                image={`${baseUrl}/images/faq-banner.jpg`}
                url={`${baseUrl}/faq`}
              />
              <FAQ />
            </>
          }
        />
        {/* Appointment Page */}
        <Route
          path="/appointment"
          element={
            <>
              <SEO
                title="Book Appointment | Krisara Gastroenterology Hyderabad"
                description="Book your consultation with expert gastroenterologists at Krisara Gastro, Hyderabad. Easy online scheduling for digestive and liver care."
                keywords="book gastro appointment, liver doctor hyderabad, digestive clinic booking, krisara appointment"
                image={`${baseUrl}/images/appointment-banner.jpg`}
                url={`${baseUrl}/appointment`}
              />
              <AppointmentPage />
            </>
          }
        />
      </Routes>
      <Footer />
    </>
  );
};
// Main App Component
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}
export default App;